import {
  RENKA_ANDROID_APPLICATION_ID,
  SAKURA_APPLICATION_ID,
} from "~/constants/app";
import { getCurrentApplicationId } from "~/lib/applicationId";
import { FIREBASE_ANALYTICS_SDK_VERSION } from "~/lib/tracking/firebase/userProperties";
import { useAuthStore } from "~/stores/useAuthStore";

type Ga4Params = Record<string, string | number>;

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

/**
 * GA4 Web 直送の対象アプリと送信先 Web ストリーム（ネイティブに Firebase 連携が無いアプリ）。
 * application_id は gtag.js の仕様で一部イベントにしか付かないため、アプリごとにストリームを分ける
 */
const getMeasurementIdByApplicationId = (): Record<
  string,
  string | undefined
> => ({
  [SAKURA_APPLICATION_ID]: import.meta.env.VITE_GA4_MEASUREMENT_ID,
  [RENKA_ANDROID_APPLICATION_ID]: import.meta.env
    .VITE_GA4_MEASUREMENT_ID_RENKA_ANDROID,
});

const GTAG_SRC = "https://www.googletagmanager.com/gtag/js";

let initializedMeasurementId: string | null = null;

const getMeasurementId = (): string | null => {
  return getMeasurementIdByApplicationId()[getCurrentApplicationId()] || null;
};

/** gtag.js を一度だけ読み込む。ページビューは screen_view を手動送信するため自動送信は切る */
const ensureGtag = (measurementId: string): void => {
  if (initializedMeasurementId === measurementId) return;
  initializedMeasurementId = measurementId;

  window.dataLayer = window.dataLayer ?? [];
  // gtag.js は arguments オブジェクトを要求するため rest 引数では動かない
  window.gtag = function gtag() {
    // biome-ignore lint/complexity/noArguments: gtag.js の仕様
    window.dataLayer?.push(arguments);
  };
  window.gtag("js", new Date());
  window.gtag("config", measurementId, { send_page_view: false });

  const script = document.createElement("script");
  script.async = true;
  script.src = `${GTAG_SRC}?id=${measurementId}`;
  document.head.appendChild(script);
};

/** GA4 へイベントを送る。対象外アプリ・Measurement ID 未設定では何もしない */
export const sendGa4Event = (eventName: string, params: Ga4Params): void => {
  try {
    const measurementId = getMeasurementId();
    if (!measurementId) return;
    ensureGtag(measurementId);
    window.gtag?.("event", eventName, params);
  } catch {
    // 補助機能のため例外は握りつぶす
  }
};

/** ログインユーザーの user_id / gender / application_id を GA4 に反映する */
export const setGa4User = (
  userId: string | null,
  gender: string | undefined,
): void => {
  try {
    const measurementId = getMeasurementId();
    if (!measurementId) return;
    ensureGtag(measurementId);
    window.gtag?.("set", { user_id: userId });
    window.gtag?.("set", "user_properties", {
      application_id: getCurrentApplicationId(),
      gender: gender ?? null,
      sdk_version: FIREBASE_ANALYTICS_SDK_VERSION,
    });
  } catch {
    // 補助機能のため例外は握りつぶす
  }
};

/** 認証ストアの変化を GA4 の user_id / gender に同期する（起動時の復元分も反映） */
export const setupGa4UserSync = (): void => {
  const sync = (): void => {
    const { userId, user } = useAuthStore.getState();
    setGa4User(userId, user?.gender);
  };
  sync();
  useAuthStore.subscribe(sync);
};

/** テスト用: 初期化状態をリセットする */
export const resetGa4State = (): void => {
  initializedMeasurementId = null;
};
